import React, { useState } from 'react';
import axios from 'axios';

interface LandingPage {
    id: string;
    urlPath: string;
    cookieName: string;
    isPublic: boolean;
    createdAt: string;
}

interface Props {
    landingPages: LandingPage[];
    selectedId: string | null;
    onSelect: (id: string) => void;
    onRefresh: () => void;
    apiBase: string;
}

// Generate unique, non-human-readable cookie name
const generateUniqueCookieName = (): string => {
    const randomId = Math.random().toString(36).substring(2, 15) +
        Math.random().toString(36).substring(2, 15);
    return `ab_${randomId}`;
};

export default function LandingPageList({
    landingPages,
    selectedId,
    onSelect,
    onRefresh,
    apiBase,
}: Props) {
    const [showForm, setShowForm] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);
    const [formData, setFormData] = useState({
        urlPath: '',
        cookieName: generateUniqueCookieName(),
        isPublic: true,
        originalUrl: '',
    });
    const [urlError, setUrlError] = useState('');

    const validateUrl = (url: string) => {
        if (!url) {
            setUrlError('URL path is required');
            return false;
        }
        // Auto-add leading slash silently
        setUrlError('');
        return true;
    };

    const normalizeUrl = (url: string) => {
        let u = url.trim();
        if (u && !u.startsWith('/')) u = '/' + u;
        // Remove trailing slash unless it's just '/'
        if (u.length > 1) u = u.replace(/\/$/, '');
        return u;
    };

    const regenerateCookie = () => {
        setFormData({ ...formData, cookieName: generateUniqueCookieName() });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const cleanPath = normalizeUrl(formData.urlPath);
        if (!cleanPath) {
            setUrlError('URL path is required');
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const payload = { ...formData, urlPath: cleanPath };
            await axios.post(`${apiBase}/admin/landing-pages`, payload);
            setSuccess(true);
            setFormData({ urlPath: '', cookieName: generateUniqueCookieName(), isPublic: true, originalUrl: '' });
            setUrlError('');

            setTimeout(() => {
                setShowForm(false);
                setSuccess(false);
                onRefresh();
            }, 1500);
        } catch (error: any) {
            console.error('Error creating landing page:', error);
            // Show the actual server error if available
            const msg = error.response?.data?.error
                || error.response?.data
                || error.message
                || 'Failed to create landing page. Check worker is deployed.';
            setError(String(msg));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="landing-page-list">
            <div className="section-header">
                <h2>Landing Pages</h2>
                <button
                    className="btn btn-primary"
                    onClick={() => {
                        setShowForm(!showForm);
                        if (showForm) {
                            setError(null);
                            setSuccess(false);
                        }
                    }}
                >
                    {showForm ? '✕ Cancel' : '+ New Page'}
                </button>
            </div>

            {showForm && (
                <div className="form-container">
                    {/* Error Alert */}
                    {error && (
                        <div className="alert alert-error">
                            <span>⚠️ {error}</span>
                            <button
                                type="button"
                                onClick={() => setError(null)}
                                className="alert-close"
                            >
                                ✕
                            </button>
                        </div>
                    )}

                    {/* Success Alert */}
                    {success && (
                        <div className="alert alert-success">
                            ✓ Landing page created successfully!
                        </div>
                    )}

                    {/* Main Form */}
                    <form className="form" onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label>
                                URL Path <span className="required">*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="e.g., /landing/summer-sale"
                                value={formData.urlPath}
                                onChange={(e) => {
                                    setFormData({ ...formData, urlPath: e.target.value });
                                    if (e.target.value) validateUrl(e.target.value);
                                }}
                                onBlur={() => validateUrl(formData.urlPath)}
                                className={urlError ? 'error' : ''}
                            />
                            <div className="help-text">
                                The URL route where this landing page will be accessed
                            </div>
                            {urlError && <div className="error-message">{urlError}</div>}
                        </div>

                        <div className="form-group">
                            <label>Original Page URL <span style={{fontSize:'0.8rem',color:'#6b7280',fontWeight:'normal'}}>(optional — for auto image loading)</span></label>
                            <input
                                type="url"
                                placeholder="e.g., https://neetprep.com/top/path-to-aiims/"
                                value={formData.originalUrl}
                                onChange={(e) => setFormData({ ...formData, originalUrl: e.target.value })}
                            />
                            <div className="help-text">
                                If set, your variant will load all images & styles from this original page automatically
                            </div>
                        </div>

                        <div className="form-group">
                            <div className="label-with-action">
                                <label>Tracking Cookie <span className="required">*</span></label>
                                <button
                                    type="button"
                                    className="btn-regenerate"
                                    onClick={regenerateCookie}
                                    title="Generate a new random cookie name"
                                >
                                    🔄 Regenerate
                                </button>
                            </div>
                            <input
                                type="text"
                                value={formData.cookieName}
                                readOnly
                                className="readonly-input"
                            />
                            <div className="help-text">
                                Auto-generated unique identifier (non-human-readable for security)
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Status</label>
                            <div className="status-toggle">
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        checked={!formData.isPublic}
                                        onChange={() => setFormData({ ...formData, isPublic: false })}
                                    />
                                    <span>⚫ Draft</span>
                                    <span className="status-info">Not accessible yet</span>
                                </label>
                                <label className="radio-option">
                                    <input
                                        type="radio"
                                        checked={formData.isPublic}
                                        onChange={() => setFormData({ ...formData, isPublic: true })}
                                    />
                                    <span>🔴 Live</span>
                                    <span className="status-info">Ready to test</span>
                                </label>
                            </div>
                        </div>

                        {/* Preview */}
                        {formData.urlPath && (
                            <div className="preview-section">
                                <h4>Preview</h4>
                                <div className="preview-content">
                                    <p><strong>Landing Page:</strong> <code>{formData.urlPath}</code></p>
                                    <p><strong>Tracking Cookie:</strong> <code>{formData.cookieName}</code></p>
                                    <p><strong>Status:</strong> {formData.isPublic ? '🔴 Live' : '⚫ Draft'}</p>
                                </div>
                            </div>
                        )}

                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                        >
                            {loading ? '⏳ Creating...' : '✓ Create Landing Page'}
                        </button>
                    </form>
                </div>
            )}

            <div className="cards-grid">
                {landingPages.length === 0 ? (
                    <div className="empty-state">
                        <p>🚀 No landing pages yet</p>
                        <p>Click "+ New Page" above to get started!</p>
                    </div>
                ) : (
                    landingPages.map((page) => {
                        // Always show the clean neetprep.in URL
                        const cleanPath = page.urlPath.startsWith('/') ? page.urlPath : '/' + page.urlPath;
                        const publicUrl = `https://neetprep.in${cleanPath}`;
                        const testUrl   = `https://neetprep.in${cleanPath}?reset_ab=1`;

                        return (
                        <div
                            key={page.id}
                            className={`card ${selectedId === page.id ? 'selected' : ''}`}
                            onClick={() => onSelect(page.id)}
                        >
                            <div className="card-header">
                                <h3>{page.urlPath}</h3>
                                <span className={`status ${page.isPublic ? 'live' : 'draft'}`}>
                                    {page.isPublic ? '🔴 Live' : '⚫ Draft'}
                                </span>
                            </div>
                            
                            <div className="public-url-box" onClick={(e) => e.stopPropagation()}>
                                <label>🌐 Public URL (neetprep.in):</label>
                                <div className="url-copy-row">
                                    <input type="text" readOnly value={publicUrl} />
                                    <button
                                        className="btn-icon"
                                        title="Copy URL"
                                        onClick={() => { navigator.clipboard.writeText(publicUrl); alert('Link copied!'); }}
                                    >
                                        📋
                                    </button>
                                </div>
                                <div className="url-actions">
                                    <a href={publicUrl} target="_blank" rel="noreferrer" className="btn btn-small">
                                        🔗 Open
                                    </a>
                                    <a href={testUrl} target="_blank" rel="noreferrer" className="btn btn-small btn-secondary" title="Force a new variant (bypasses cookie)">
                                        🔄 Test New Variant
                                    </a>
                                </div>
                            </div>

                            <p className="meta" style={{ marginTop: '1rem' }}>
                                <strong>Tracking Cookie:</strong> <code>{page.cookieName}</code>
                            </p>
                            
                            <button
                                className="btn btn-primary"
                                style={{ width: '100%', marginTop: '0.5rem' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onSelect(page.id);
                                }}
                            >
                                Manage Variants & Analytics →
                            </button>
                        </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
